import React, { useState, useEffect } from "react";
import { getPageViews } from "../utils/adminGetPageViews";
import { formatDateDDMMMYYYY } from "../utils/dateHelpers";

const AdminPageView = () => {
  // --- Data States ---
  const [pageViews, setPageViews] = useState([]);
  const [filteredPageViews, setFilteredPageViews] = useState([]);
  const [sortedPageViews, setSortedPageViews] = useState([]);
  const [visibleCount, setVisibleCount] = useState(25);
  const [visiblePageViews, setVisiblePageViews] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("ProductView"); // Default tab

  // --- Loading & Error States ---
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch page views data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getPageViews();
        setPageViews(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Update filteredPageViews whenever pageViews, searchTerm, or activeTab changes
  useEffect(() => {
    let filtered = pageViews;

    // Filter by active tab (type)
    if (activeTab) {
      filtered = filtered.filter((item) => item.type === activeTab);
    }

    // Filter by search term (type or email)
    if (searchTerm) {
      filtered = filtered.filter((item) => {
        const term = searchTerm.toLowerCase();
        const typeMatch = item.type && item.type.toLowerCase().includes(term);
        const emailMatch =
          item.email && item.email.toLowerCase().includes(term);
        return typeMatch || emailMatch;
      });
    }
    setFilteredPageViews(filtered);
    setVisibleCount(25); // reset visible count when filter changes
  }, [searchTerm, pageViews, activeTab]);

  // Sort the filtered data by eventTime (newest first)
  useEffect(() => {
    const sorted = [...filteredPageViews].sort(
      (a, b) => new Date(b.eventTime) - new Date(a.eventTime)
    );
    setSortedPageViews(sorted);
  }, [filteredPageViews]);

  // Update the visible data based on visibleCount (for infinite scroll)
  useEffect(() => {
    setVisiblePageViews(sortedPageViews.slice(0, visibleCount));
  }, [sortedPageViews, visibleCount]);

  // Infinite scroll handler: Increase visibleCount when scrolled near bottom
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      if (visibleCount < sortedPageViews.length) {
        setVisibleCount((prev) => prev + 25);
      }
    }
  };

  // Show spinner while loading data
  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="spin-loader"></div>
      </div>
    );
  }

  // Render error message if fetching fails
  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  return (
    <div>
      {/* Tab Buttons */}
      <div className="flex space-x-4 md:px-8 px-5 mb-6">
        <button
          onClick={() => setActiveTab("ProductView")}
          className={`px-4 py-2 rounded-full ${
            activeTab === "ProductView"
              ? "bg-blue-600 text-white border border-gray-500"
              : "bg-white text-black"
          } main-class px-8 py-2.5 rounded-full text-sm`}
        >
          Product View
        </button>
        <button
          onClick={() => setActiveTab("LandingPageView")}
          className={`px-4 py-2 rounded-full ${
            activeTab === "LandingPageView"
              ? "bg-blue-600 text-white border border-gray-500"
              : "bg-white text-black"
          } main-class px-8 py-2.5 rounded-full text-sm`}
        >
          Landing Page View
        </button>
      </div>

      {/* Search Box for Type and Email */}
      <div className="flex space-x-4 md:px-8 px-5 mb-6">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by type or email"
          className="w-[300px] border text-[15px] flex justify-center items-center px-3 border-gray-200 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Table container with infinite scroll */}
      <div
        className="overflow-y-auto max-h-[calc(100vh-305px)] scroll-bar"
        onScroll={handleScroll}
      >
        <table className="min-w-full text-sm">
          <thead className="bg-[#EFF4FF] text-[#929EB6] border-b border-[#D1DFFC] sticky top-0">
            <tr>
              <th className="p-4 text-left font-medium">S.No</th>
              <th className="p-4 text-left font-medium">Type</th>
              <th className="p-4 text-left font-medium">Email</th>
              <th className="p-4 text-left font-medium">Page URL</th>
              <th className="p-4 text-left font-medium">Event Time</th>
            </tr>
          </thead>
          <tbody>
            {visiblePageViews.length > 0 ? (
              visiblePageViews.map((item, index) => (
                <tr
                  key={item.id}
                  className={`${
                    index % 2 === 0 ? "bg-[#F4F7FF]" : "bg-[#EBF1FF]"
                  } border-b border-[#DCE2EC]`}
                >
                  <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                    {index + 1}
                  </td>
                  <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                    {item.type}
                  </td>
                  <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                    {item.email || "-"}
                  </td>
                  <td className="p-4 text-[#193B68] xl:text-sm lg:text-xs">
                    <a
                      href={item.value}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-500 hover:text-blue-600"
                    >
                      {item.value}
                    </a>
                  </td>
                  <td className="p-4 whitespace-nowrap text-[#193B68] xl:text-sm lg:text-xs">
                    {formatDateDDMMMYYYY(item.eventTime)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="6"
                  className="py-5 px-4 bg-[#F4F7FF]/80 text-center text-gray-500"
                >
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPageView;
